import { logger } from '@/services/logger';

interface ServiceWorkerConfig {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

class ServiceWorkerManager {
  private isRegistered = false;
  private registration: ServiceWorkerRegistration | null = null;

  public async register(config: ServiceWorkerConfig = {}) {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      logger.warn('Service Worker not supported in this environment');
      return;
    }

    // Only register in production or when explicitly enabled
    if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_SW) {
      logger.info('Service Worker disabled in development');
      return;
    }

    try {
      logger.info('Registering Service Worker');
      
      const registration = await navigator.serviceWorker.register('/sw.js');
      this.registration = registration;
      this.isRegistered = true;

      logger.info('Service Worker registered successfully', {
        scope: registration.scope,
        updateViaCache: registration.updateViaCache
      });

      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        logger.info('New Service Worker installing');

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New update available
              logger.info('New Service Worker update available');
              config.onUpdate?.(registration);
            } else {
              // Content cached for first time
              logger.info('Service Worker content cached for first time');
              config.onSuccess?.(registration);
            }
          }
        });
      });

      // Listen for controlling service worker changes
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        logger.info('Service Worker controller changed');
        window.location.reload();
      });

      // Handle service worker messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event);
      });

      config.onSuccess?.(registration);

    } catch (error) {
      logger.error('Service Worker registration failed', { error });
      config.onError?.(error as Error);
    }
  }

  public async unregister(): Promise<boolean> {
    if (!this.registration) {
      logger.warn('No Service Worker registration to unregister');
      return false;
    }

    try {
      const result = await this.registration.unregister();
      logger.info('Service Worker unregistered', { result });
      this.isRegistered = false;
      this.registration = null;
      return result;
    } catch (error) {
      logger.error('Failed to unregister Service Worker', { error });
      return false;
    }
  }

  public async update(): Promise<void> {
    if (!this.registration) {
      logger.warn('No Service Worker registration to update');
      return;
    }

    try {
      await this.registration.update();
      logger.info('Service Worker update triggered');
    } catch (error) {
      logger.error('Failed to update Service Worker', { error });
    }
  }

  public async skipWaiting(): Promise<void> {
    if (!this.registration?.waiting) {
      logger.warn('No waiting Service Worker to skip');
      return;
    }

    try {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      logger.info('Requested Service Worker to skip waiting');
    } catch (error) {
      logger.error('Failed to skip Service Worker waiting', { error });
    }
  }

  public async syncData(): Promise<void> {
    if (!this.isOnline() || !this.registration) {
      logger.warn('Cannot sync data: offline or no registration');
      return;
    }

    // Check if background sync is supported
    if (!('serviceWorker' in navigator) || !('sync' in window.ServiceWorkerRegistration.prototype)) {
      logger.warn('Background sync not supported');
      return;
    }

    try {
      await (this.registration as any).sync.register('sync-data');
      logger.info('Background sync scheduled');
    } catch (error) {
      logger.error('Failed to schedule background sync', { error });
    }
  }

  public async requestPushPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      logger.warn('Notifications not supported');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      logger.info('Notification permission requested', { permission });
      return permission;
    }

    return Notification.permission;
  }

  public isOnline(): boolean {
    return navigator.onLine;
  }

  public getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  public isServiceWorkerRegistered(): boolean {
    return this.isRegistered;
  }

  private handleServiceWorkerMessage(event: MessageEvent) {
    const { data } = event;
    
    logger.debug('Received message from Service Worker', { data });

    switch (data.type) {
      case 'CACHE_UPDATED':
        logger.info('Service Worker cache updated', { urls: data.urls });
        break;
      
      case 'OFFLINE':
        logger.warn('Application is offline');
        this.notifyOfflineStatus(true);
        break;
      
      case 'ONLINE':
        logger.info('Application is back online');
        this.notifyOfflineStatus(false);
        break;
      
      case 'SYNC_COMPLETE':
        logger.info('Background sync completed', { results: data.results });
        break;
      
      case 'ERROR':
        logger.error('Service Worker error', { error: data.error });
        break;
      
      default:
        logger.debug('Unknown Service Worker message', { data });
    }
  }

  private notifyOfflineStatus(isOffline: boolean) {
    // Dispatch custom events for offline/online status
    const event = new CustomEvent(isOffline ? 'app-offline' : 'app-online', {
      detail: { timestamp: new Date() }
    });
    window.dispatchEvent(event);
  }

  public setupOfflineDetection() {
    window.addEventListener('online', () => {
      logger.info('Browser detected online status');
      this.notifyOfflineStatus(false);
    });

    window.addEventListener('offline', () => {
      logger.warn('Browser detected offline status');
      this.notifyOfflineStatus(true);
    });
  }
}

// Create singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Convenience functions
export const registerServiceWorker = (config?: ServiceWorkerConfig) => 
  serviceWorkerManager.register(config);

export const unregisterServiceWorker = () => 
  serviceWorkerManager.unregister();

export const updateServiceWorker = () => 
  serviceWorkerManager.update();

export const skipWaitingServiceWorker = () => 
  serviceWorkerManager.skipWaiting();

export const isServiceWorkerSupported = () => 
  typeof window !== 'undefined' && 'serviceWorker' in navigator; 